import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import { Delete, Edit, Add, CheckCircle } from '@mui/icons-material';

const GlossaryTranslationDialog = ({ 
  open, 
  onClose, 
  onSaved, 
  term, 
  translation, 
  languages 
}) => {
  const [mode, setMode] = useState('list'); // 'list' or 'form'
  const [existingTranslations, setExistingTranslations] = useState([]);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [formData, setFormData] = useState({
    glossary_term: term?.id,
    language: '',
    translated_text: '',
    is_active: true
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (term) {
      loadTranslations();
    }
  }, [term]);

  useEffect(() => {
    if (translation) {
      setMode('form');
      setEditingTranslation(translation);
      setFormData({
        glossary_term: term.id,
        language: translation.language?.id || translation.language,
        translated_text: translation.translated_text,
        is_active: translation.is_active
      });
    } else if (mode === 'list') {
      resetForm();
    }
  }, [translation, term, mode]);

  const loadTranslations = async () => {
    try {
      const response = await axios.get(`terminology/glossary-terms/${term.id}/translations/`);
      setExistingTranslations(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading translations:', error);
      // If endpoint doesn't exist, use translations from term object
      if (term.translations) {
        setExistingTranslations(term.translations);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      glossary_term: term?.id,
      language: '',
      translated_text: '',
      is_active: true
    });
    setEditingTranslation(null);
  };

  const handleSubmit = async () => {
    if (!formData.language || !formData.translated_text) {
      setError('Language and translated text are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare payload with language_id for write operations
      const payload = {
        glossary_term: formData.glossary_term,
        language_id: formData.language,
        translated_text: formData.translated_text,
        is_active: formData.is_active
      };

      if (editingTranslation) {
        await axios.put(`terminology/glossary-term-translations/${editingTranslation.id}/`, payload);
      } else {
        await axios.post('terminology/glossary-term-translations/', payload);
      }
      
      await loadTranslations();
      setMode('list');
      resetForm();
      
      if (existingTranslations.length === 0 && mode === 'form') {
        // If this was the first translation, close the dialog
        onSaved();
      }
    } catch (error) {
      console.error('Error saving translation:', error);
      const errorMsg = error.response?.data?.message || 
                       error.response?.data?.detail || 
                       error.response?.data?.language?.[0] ||
                       'Error saving translation';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (trans) => {
    setEditingTranslation(trans);
    setFormData({
      glossary_term: term.id,
      language: trans.language?.id || trans.language,
      translated_text: trans.translated_text,
      is_active: trans.is_active
    });
    setMode('form');
  };

  const handleDelete = async (transId) => {
    if (!window.confirm('Are you sure you want to delete this translation?')) {
      return;
    }
    
    try {
      await axios.delete(`terminology/glossary-term-translations/${transId}/`);
      await loadTranslations();
    } catch (error) {
      setError(error.response?.data?.message || 'Error deleting translation');
    }
  };

  const getLanguageName = (languageId) => {
    // Handle case where languageId is an object
    const id = typeof languageId === 'object' ? languageId?.id : languageId;
    const lang = languages.find(l => l.id === id || l.code === id);
    return lang ? `${lang.name} (${lang.name_native})` : (id || 'Unknown');
  };

  const getAvailableLanguages = () => {
    const usedLanguageIds = existingTranslations.map(t => t.language?.id || t.language);
    return languages.filter(l => !usedLanguageIds.includes(l.id) || l.id === formData.language);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'list' 
          ? `Translations for: ${term?.source_text}` 
          : (editingTranslation ? 'Edit Translation' : 'Add Translation')
        }
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        
        {mode === 'list' ? (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                Source: <strong>{term?.source_text}</strong> ({term?.source_language?.name || term?.source_language?.code || 'Unknown'})
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setMode('form')}
                size="small"
                disabled={getAvailableLanguages().length === 0}
              >
                Add Translation
              </Button>
            </Box>
            
            {existingTranslations.length === 0 ? (
              <Alert severity="info">
                No translations yet. Click "Add Translation" to create one.
              </Alert>
            ) : (
              <List>
                {existingTranslations.map((trans, index) => (
                  <React.Fragment key={trans.id}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1">
                              {trans.translated_text}
                            </Typography>
                            {trans.is_active && (
                              <Chip label="Active" size="small" color="success" icon={<CheckCircle />} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            Language: {getLanguageName(trans.language)}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleEdit(trans)} sx={{ mr: 1 }}>
                          <Edit />
                        </IconButton>
                        <IconButton edge="end" color="error" onClick={() => handleDelete(trans.id)}>
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </>
        ) : (
          <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Source: <strong>{term?.source_text}</strong>
            </Alert>
            
            <FormControl fullWidth required>
              <InputLabel>Target Language</InputLabel>
              <Select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                label="Target Language"
                disabled={!!editingTranslation}
              >
                {getAvailableLanguages().map((lang) => (
                  <MenuItem key={lang.id} value={lang.id}>
                    {lang.name} ({lang.name_native})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Translated Text"
              value={formData.translated_text}
              onChange={(e) => setFormData({ ...formData, translated_text: e.target.value })}
              required
              autoFocus={!editingTranslation}
              helperText="The translation in the target language"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        {mode === 'form' && existingTranslations.length > 0 && (
          <Button 
            onClick={() => {
              setMode('list');
              resetForm();
            }} 
            disabled={loading}
          >
            Back to List
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>
          {mode === 'list' ? 'Close' : 'Cancel'}
        </Button>
        {mode === 'form' && (
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading || !formData.language || !formData.translated_text}
          >
            {loading ? 'Saving...' : (editingTranslation ? 'Update' : 'Create')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GlossaryTranslationDialog;
